/* import __COLOCATED_TEMPLATE__ from './manage-export-schedule-side-drawer.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import { type Task, type TaskGenerator } from 'ember-concurrency';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { trackedTask } from 'ember-resources/util/ember-concurrency';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import type DatasetExportSchedule from 'embercom/models/reporting/dataset-export-schedule';
import { tracked } from '@glimmer/tracking';
import type DatasetExportScheduleService from 'embercom/services/dataset-export-schedule-service';

interface Args {
  onClose: () => void;
  isOpen: boolean;
}

export default class ManageExportScheduleSideDrawer extends Component<Args> {
  @service declare store: Store;
  @service declare intl: IntlService;
  @service notificationsService: any;
  @service declare datasetExportScheduleService: DatasetExportScheduleService;

  @tracked sideDrawerOpen = false;
  @tracked selectedSchedule: DatasetExportSchedule | null = null;

  fetchDataTask: Task<any, any> = taskFor(this.fetchData);

  tableData = trackedTask(this, this.fetchDataTask, () => []);

  @dropTask
  *fetchData(): TaskGenerator<any> {
    let schedules = yield this.store.findAll('reporting/dataset-export-schedule');
    return schedules.filter((schedule: DatasetExportSchedule) => !schedule.isNew);
  }

  get data() {
    return (this.tableData?.value as any[])?.rejectBy('isDeleted') || [];
  }

  get columns() {
    return [
      {
        label: this.intl.t('reporting.data-export.schedule-export.schedule-name'),
        valuePath: 'name',
        width: '50%',
      },
      {
        label: this.intl.t('reporting.data-export.schedule-export.frequency'),
        valuePath: 'frequency',
        width: '50%',
      },
    ];
  }

  private frequencyPrefix(schedule: DatasetExportSchedule) {
    return this.datasetExportScheduleService.customLabel(schedule);
  }

  @action
  buildFrequency(schedule: DatasetExportSchedule) {
    return this.frequencyPrefix(schedule);
  }

  @action
  editSchedule(row: DatasetExportSchedule) {
    this.selectedSchedule = row;
    this.sideDrawerOpen = true;
  }

  @action
  closeSideDrawer() {
    this.sideDrawerOpen = false;
    this.selectedSchedule = null;
  }

  @action
  deleteSchedule(row: DatasetExportSchedule) {
    row.destroyRecord();
    this.notificationsService.notifyConfirmation(
      this.intl.t('reporting.data-export.schedule-export.manage-schedule.delete-success-message'),
    );
  }

  get hasNoSchedules() {
    return (this.tableData?.value as any[])?.length === 0;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::ManageExportScheduleSideDrawer': typeof ManageExportScheduleSideDrawer;
    'reporting/manage-export-schedule-side-drawer': typeof ManageExportScheduleSideDrawer;
  }
}
